import { AuthProvider, useAuth } from "./context/AuthContext";
import AdminRoute from "./components/AdminRoute";
import React, { useState, useEffect, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import Navbar from "./components/Navbar";
import { ErrorProvider } from "./context/ErrorContext";
import { LanguageProvider } from "./i18n";
import AnnouncementModal from "./components/AnnouncementModal";

// Lazy loading all pages
const UploadPage = React.lazy(() => import("./pages/UploadPage"));
const LoadingPage = React.lazy(() => import("./pages/LoadingPage"));
const AwarenessPage = React.lazy(() => import("./pages/AwarenessPage"));
const LogInPage = React.lazy(() => import("./pages/LogInPage"));
const RegistrationPage = React.lazy(() => import("./pages/RegistrationPage"));
const ProfilePage = React.lazy(() => import("./pages/ProfilePage"));
const LandingPage = React.lazy(() => import("./pages/LandingPage"));
const ResultsContainer = React.lazy(() => import("./pages/ResultsContainer"));
const FAQ = React.lazy(() => import("./pages/FAQPage"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const TermsandConditions = React.lazy(() => import("./pages/TermsandConditions"));
const AdminLayout = React.lazy(() => import("./pages/AdminLayout"));
const DashboardOverview = React.lazy(() => import("./pages/DashboardOverview"));
const UserManagement = React.lazy(() => import("./pages/UserManagement"));
const ContentManagement = React.lazy(() => import("./pages/ContentManagement"));
const SystemManagement = React.lazy(() => import("./pages/SystemManagement"));
const AuditLogs = React.lazy(() => import("./pages/AuditLogs"));
const FlaggedResults = React.lazy(() => import("./pages/FlaggedResults"));
const HowItWorksPage = React.lazy(() => import("./pages/HowitWorksPage"));
const OTPVerificationPage = React.lazy(() => import("./pages/OTPVerificationPage"));
const ForgotPasswordPage = React.lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPassPage = React.lazy(() => import("./pages/ResetPassPage"));
// A reporter answers an admin's request for the original video or a source (link from the email)
const ReportInfoPage = React.lazy(() => import("./pages/ReportInfoPage"));

function AppContent() {
  const location = useLocation();
  const { checkSession } = useAuth(); // ✨ FIX: Destructure checkSession from AuthContext
  const [activeAnnouncement, setActiveAnnouncement] = useState(null);

  console.log(location.pathname);

  useEffect(() => {
    // This fires every time the user clicks a link to change pages
    checkSession();
  }, [location.pathname, checkSession]);

  // Global Announcement Fetch Effect
  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const baseURL =
          process.env.REACT_APP_API_URL || "http://localhost:5000";
        const res = await fetch(`${baseURL}/api/public/announcement`);

        if (res.ok) {
          const data = await res.json();

          if (data && data.announcement_id) {
            const seenId = sessionStorage.getItem("seenAnnouncement");
            if (seenId !== data.announcement_id) {
              setActiveAnnouncement(data);
            }
          } else {
            setActiveAnnouncement(null);
          }
        }
      } catch (error) {
        console.error("Failed to load announcement", error);
      }
    };

    fetchAnnouncement();

    const intervalId = setInterval(fetchAnnouncement, 60000);

    return () => clearInterval(intervalId);
  }, []);

  const handleCloseAnnouncement = () => {
    if (activeAnnouncement) {
      sessionStorage.setItem(
        "seenAnnouncement",
        activeAnnouncement.announcement_id,
      );
    }
    setActiveAnnouncement(null);
  };

  const showNavbarPaths = [
    "/",
    "/ai-awareness",
    "/profile",
    "/scan",
    "/how-it-works",
    "/faq",
    "/privacy-policy",
    "/terms-and-conditions",
  ];
  const shouldShowNavbar =
    showNavbarPaths.includes(location.pathname) ||
    location.pathname.startsWith("/results/");
  const isAdminRoute = location.pathname.startsWith("/admin");
  return (
    <div className="app-container">
      {shouldShowNavbar && <Navbar />}

      {shouldShowNavbar && (
        <div style={{ height: "67px", width: "100%" }}></div>
      )}

      <main className="page-content">
        <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/scan" element={<UploadPage />} />
            <Route path="/loading/:id" element={<LoadingPage />} />
            <Route path="/results/:id" element={<ResultsContainer />} />
            <Route path="/log-in" element={<LogInPage />} />
            <Route path="/register" element={<RegistrationPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/ai-awareness" element={<AwarenessPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route
              path="/terms-and-conditions"
              element={<TermsandConditions />}
            />
            <Route path="/verify-otp" element={<OTPVerificationPage />} />
            <Route path="/reset-password" element={<ResetPassPage />} />
            <Route path="/report-info/:token" element={<ReportInfoPage />} />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              }
            >
              <Route index element={<DashboardOverview />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="content" element={<ContentManagement />} />
              <Route path="system" element={<SystemManagement />} />
              <Route path="audit-logs" element={<AuditLogs />} />
              <Route path="flagged-results" element={<FlaggedResults />} />
              {/* The triage queue is part of Flagged Results now (system-flagged cases) */}
              <Route
                path="flagged-triage-queue"
                element={<Navigate to="/admin/flagged-results?source=system&view=patterns" replace />}
              />
            </Route>
          </Routes>
        </Suspense>
      </main>

      {/* Global Announcement Popup Modal */}
      {activeAnnouncement && !isAdminRoute && (
        <AnnouncementModal
          isOpen={!!activeAnnouncement}
          onClose={handleCloseAnnouncement}
          title={activeAnnouncement.title}
          description={activeAnnouncement.message}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <ErrorProvider>
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </ErrorProvider>
    </LanguageProvider>
  );
}

export default App;
